import { CompanyFacts } from "../../components/CompanyFacts";
import { Icon } from "../../components/Icon";
import { CABLE_SERVICE_FEE_NAIRA, COMPANY, WHATSAPP } from "../../config/company";
import { CompanyLayout, InfoSection, SafetyNote, Tile } from "./CompanyLayout";

const PRINCIPLES = [
  {
    title: "Your PIN before any money moves",
    body: "Every purchase, Ejor payment, gift and schedule is confirmed with your transaction PIN, typed into a secure form inside WhatsApp. Our assistant never sees it.",
  },
  {
    title: "A failed card is never retried",
    body: "If your bank declines a payment, we stop. A new charge only happens when you ask for it again and confirm with your PIN.",
  },
  {
    title: "Not delivered means refunded",
    body: "If your card was charged and the airtime, data, subscription or token didn’t arrive, the full amount goes back to your card automatically.",
  },
  {
    title: "No wallet, no stored balance",
    body: "We are not a bank and don’t hold your money. Each payment is charged to your own card through Paystack, and we never store your full card number.",
  },
];

export function AboutPage() {
  return (
    <CompanyLayout
      path="/about"
      eyebrow="About & contact"
      title={
        <>
          Bills, paid in
          <br />a message.
        </>
      }
      intro={
        <>
          {COMPANY.brandName} is a bill-payment assistant on WhatsApp, built and
          run in Lagos by {COMPANY.legalName}. You text what you need; we show
          you the price and pay it once you confirm with your PIN.
        </>
      }
      actions={
        <>
          <a className="btn btn-accent btn-lg" href={WHATSAPP.href}>
            <Icon name="chat" />
            Start on WhatsApp
          </a>
          <a className="btn btn-ghost btn-lg" href="/support">
            Contact support
          </a>
        </>
      }
      aside={
        <div className="stat-stack">
          <div>
            <strong>4</strong>
            <span>bill types: airtime, data, cable TV and electricity</span>
          </div>
          <div>
            <strong>24/7</strong>
            <span>the assistant answers any time</span>
          </div>
          <div>
            <strong>₦0</strong>
            <span>
              extra on airtime and data; ₦{CABLE_SERVICE_FEE_NAIRA} on cable;
              only the card fee on electricity
            </span>
          </div>
        </div>
      }
    >
      <InfoSection
        title="Why we built Hethera"
        lede="Most people in Nigeria already live in WhatsApp. Paying a bill shouldn't mean leaving it."
      >
        <div className="prose prose--wide">
          <p>
            Topping up a phone, renewing GOtv or buying a meter token usually
            means another app, another password and another wallet to fund.
            Hethera does it in the chat you already have open: say “recharge my
            line with ₦1,000” or “renew my DStv”, check the details and confirm
            with your PIN.
          </p>
          <p>
            Around that we added the things people actually do with bills:
            asking someone you trust to pay one (<a href="/#ejor">Ejor</a>),
            sending airtime or electricity as a gift (<a href="/#dash">Dash</a>
            ), putting regular bills on a schedule (
            <a href="/#schedules">Scheduled payments</a>) and seeing what you
            spent (<a href="/#reports">Reports</a>).
          </p>
        </div>
      </InfoSection>

      <InfoSection
        title="How we look after your money"
        lede="Rules built into the product, not promises."
      >
        <ol className="principles">
          {PRINCIPLES.map((p) => (
            <li key={p.title}>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </li>
          ))}
        </ol>
      </InfoSection>

      <InfoSection
        title="Who we work with"
        lede="Licensed partners handle the money and the bills; we handle the conversation."
      >
        <div className="tile-grid">
          <Tile icon="card" title="Paystack">
            Processes every card payment and refund. Your card is saved with
            Paystack, not with us.
          </Tile>
          <Tile icon="bolt" title="VTpass">
            Delivers airtime, data, cable TV subscriptions and electricity
            tokens for MTN, Airtel, Glo, 9mobile, DStv, GOtv, StarTimes and the
            electricity distribution companies.
          </Tile>
          <Tile icon="chat" title="WhatsApp Business Platform">
            Carries the conversation. Hethera is an independent business and
            isn’t affiliated with Meta.
          </Tile>
        </div>
      </InfoSection>

      <InfoSection
        title="Company details"
        lede="As registered with the Corporate Affairs Commission."
      >
        <CompanyFacts />
      </InfoSection>

      <InfoSection
        title="Get in touch"
        lede="Pick whatever is easiest. The assistant can also answer most questions straight away."
      >
        <div className="tile-grid">
          <Tile
            icon="mail"
            title="Email support"
            href="/support"
            cta={COMPANY.supportEmail}
          >
            For payment problems, refunds, your account or anything else.
            Include your reference and we’ll sort it out.
          </Tile>
          <Tile
            icon="call"
            title="Phone & office"
            href="/contact"
            cta={COMPANY.phoneDisplay}
          >
            Call us during support hours, or find our registered office in
            Surulere, Lagos.
          </Tile>
          <Tile
            icon="chat"
            title="WhatsApp"
            href="/whatsapp"
            cta="Open the chat"
          >
            Start using Hethera, check you’re talking to the official account
            and learn the shortcuts.
          </Tile>
        </div>
        <SafetyNote />
      </InfoSection>
    </CompanyLayout>
  );
}
